import { zodResolver } from '@hookform/resolvers/zod'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Input, NumericTextBox } from '@progress/kendo-react-inputs'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useToggle } from 'react-use'
import * as R from 'remeda'
import { z } from 'zod'

import { Box } from '~/components/elements/Box'
import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import { HStack } from '~/components/elements/HStack'
import { StyledButton } from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { type SurveyMetadataInput } from '~/gql/graphql'
import { useFormField } from '~/hooks/useFormField'
import { type Survey } from '~/hooks/useSurveyForSettingsQuery'
import { exists, SurveyOp } from '~/utils'

import { SurveySettingsFormInput } from './components/SurveySettingsFormInput'
import styles from './SurveySettingsForm.css'

type SelectOption = {
  key: string
  label: string
}

// 日本語/英語からキーへのマッピング（データマイグレーション期間中の互換性のため）
// prettier-ignore
const VALUE_TO_KEY_MAP: Record<string, string> = {
  // 共通
  '（未選択）': 'not_selected',
  '(not Selected)': 'not_selected',
  // 課金形態
  'サブスク型': 'subscription_type',
  'Subscription': 'subscription_type',
  '都度購入型': 'perpurchase_type',
  'One-time Purchase': 'perpurchase_type',
  '完全無料': 'completely_free',
  'Completely Free': 'completely_free',
  'その他': 'other',
  'Other': 'other',
  // 購入意思決定頻度
  '週１回以上': 'once_a_week_or_more',
  'Once a Week or More': 'once_a_week_or_more',
  '月１回以上': 'once_a_month_or_more',
  'Once a Month or More': 'once_a_month_or_more',
  '年１回以上': 'once_a_year_or_more',
  'Once a Year or More': 'once_a_year_or_more',
  '継続利用': 'continuous_use',
  'Continuous Use': 'continuous_use',
  // 無料利用の有無
  '有り': 'yes',
  'Yes': 'yes',
  '無し': 'no',
  'No': 'no',
  // 積極/消極定義
  'SA': 'SA',
  'MA': 'MA',
  // 購入・利用チャネル
  'オンライン中心': 'onlinecentered',
  'Online-centered': 'onlinecentered',
  'オフライン中心': 'offlinecentered',
  'Offline-centered': 'offlinecentered',
  'どちらも重要': 'both_important',
  'Both Important': 'both_important',
  // 営業経由での購入・利用
  '営業経由での購入・利用が中心': 'purchasesusage_mainly_through_sales',
  'Purchases/usage mainly through sales': 'purchasesusage_mainly_through_sales',
  '営業を経由しない購入・利用が中心': 'purchasesusage_mainly_without_sales',
  'Purchases/usage mainly without sales': 'purchasesusage_mainly_without_sales',
  // 業種
  '食品': 'food',
  'Food': 'food',
  '繊維': 'textile',
  'Textile': 'textile',
  'パルプ・紙': 'pulp_paper',
  'Pulp & Paper': 'pulp_paper',
  '化学': 'chemical',
  'Chemical': 'chemical',
  '医薬品': 'pharmaceutical',
  'Pharmaceutical': 'pharmaceutical',
  '石油': 'petroleum',
  'Petroleum': 'petroleum',
  'ゴム': 'rubber',
  'Rubber': 'rubber',
  '窯業': 'ceramics',
  'Ceramics': 'ceramics',
  '鉄鋼': 'steel',
  'Steel': 'steel',
  '非鉄金属': 'nonferrous_metal',
  'Non-ferrous Metal': 'nonferrous_metal',
  '機械': 'machinery',
  'Machinery': 'machinery',
  '電気機器': 'electrical_equipment',
  'Electrical Equipment': 'electrical_equipment',
  '造船': 'shipbuilding',
  'Shipbuilding': 'shipbuilding',
  '自動車': 'automobile',
  'Automobile': 'automobile',
  '輸送用機器': 'transportation_equipment',
  'Transportation Equipment': 'transportation_equipment',
  '精密機器': 'precision_equipment',
  'Precision Equipment': 'precision_equipment',
  'その他製造': 'other_manufacturing',
  'Other Manufacturing': 'other_manufacturing',
  '農林・水産': 'agriculture_forestry_fishery',
  'Agriculture, Forestry & Fishery': 'agriculture_forestry_fishery',
  '鉱業': 'mining',
  'Mining': 'mining',
  '建設': 'construction',
  'Construction': 'construction',
  '商社': 'trading_company',
  'Trading Company': 'trading_company',
  '小売業': 'retail',
  'Retail': 'retail',
  '銀行': 'banking',
  'Banking': 'banking',
  '証券': 'securities',
  'Securities': 'securities',
  '保険': 'insurance',
  'Insurance': 'insurance',
  'その他金融': 'other_finance',
  'Other Finance': 'other_finance',
  '不動産': 'real_estate',
  'Real Estate': 'real_estate',
  '鉄道・バス': 'railway_bus',
  'Railway & Bus': 'railway_bus',
  '陸運': 'land_transportation',
  'Land Transportation': 'land_transportation',
  '海運': 'marine_transportation',
  'Marine Transportation': 'marine_transportation',
  '空運': 'air_transportation',
  'Air Transportation': 'air_transportation',
  '倉庫・運輸': 'warehouse_logistics',
  'Warehouse & Logistics': 'warehouse_logistics',
  '通信': 'telecommunications',
  'Telecommunications': 'telecommunications',
  '電力': 'electric_power',
  'Electric Power': 'electric_power',
  'ガス': 'gas',
  'Gas': 'gas',
  'ToCサービス': 'toc_service',
  'ToC Service': 'toc_service',
  'ToBサービス': 'tob_service',
  'ToB Service': 'tob_service',
}

/**
 * DBから取得した値（日本語/英語/キー）をキーに正規化する
 * データマイグレーション完了後は不要になる
 *
 * @param value - 正規化する値
 * @returns 正規化されたキー
 */
const normalizeValueToKey = (value: string | null | undefined): string => {
  if (!value) {
    return 'not_selected'
  }

  // 日本語または英語からキーに変換、マップにない場合はそのまま返す
  return VALUE_TO_KEY_MAP[value] ?? value
}

const schema = z.object({
  title: z.string().min(1),
  surveyStartDate: z.date().nullable(),
  tamName: z.string(),
  tamNumber: z.number(),
  billingTypeName: z.string(),
  purchaseDecisionFrequencyName: z.string(),
  isFreeName: z.string(),
  definitionTypeName: z.string(),
  purchaseMainChannelName: z.string(),
  isUseViaSales: z.string(),
  industryName: z.string(),
})
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export type SurveySettingsFormProps = {
  'data-testid'?: string
  onRemove?: () => void
  onSave?: (data: { title: string; metadata: SurveyMetadataInput }) => void
  survey?: Survey
}

export const SurveySettingsForm = ({
  'data-testid': dataTestId = 'components-features-survey-settings-form',
  onRemove,
  onSave,
  survey,
}: SurveySettingsFormProps) => {
  const { t } = useTranslation()
  const metadata = survey?.metadata

  const billingTypeNames = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      { key: 'subscription_type', label: t('subscription_type') },
      { key: 'perpurchase_type', label: t('perpurchase_type') },
      { key: 'completely_free', label: t('completely_free') },
      { key: 'other', label: t('other') },
    ],
    [t],
  )

  const purchaseDecisionFrequencyNames = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      { key: 'once_a_week_or_more', label: t('once_a_week_or_more') },
      { key: 'once_a_month_or_more', label: t('once_a_month_or_more') },
      { key: 'once_a_year_or_more', label: t('once_a_year_or_more') },
      { key: 'continuous_use', label: t('continuous_use') },
      { key: 'other', label: t('other_1') },
    ],
    [t],
  )

  const isFreeNames = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      { key: 'yes', label: t('yes') },
      { key: 'no', label: t('no') },
    ],
    [t],
  )

  const definitionTypeNames = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      { key: 'SA', label: 'SA' },
      { key: 'MA', label: 'MA' },
      { key: 'other', label: t('other_2') },
    ],
    [t],
  )

  const purchaseMainChannelNames = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      { key: 'onlinecentered', label: t('onlinecentered') },
      { key: 'offlinecentered', label: t('offlinecentered') },
      { key: 'both_important', label: t('both_important') },
    ],
    [t],
  )

  const isUseViaSalesArray = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      {
        key: 'purchasesusage_mainly_through_sales',
        label: t('purchasesusage_mainly_through_sales'),
      },
      {
        key: 'purchasesusage_mainly_without_sales',
        label: t('purchasesusage_mainly_without_sales'),
      },
    ],
    [t],
  )

  const industryNames = React.useMemo<SelectOption[]>(
    () => [
      { key: 'not_selected', label: t('not_selected') },
      { key: 'food', label: t('food') },
      { key: 'textile', label: t('textile') },
      { key: 'pulp_paper', label: t('pulp_paper') },
      { key: 'chemical', label: t('chemical') },
      { key: 'pharmaceutical', label: t('pharmaceutical') },
      { key: 'petroleum', label: t('petroleum') },
      { key: 'rubber', label: t('rubber') },
      { key: 'ceramics', label: t('ceramics') },
      { key: 'steel', label: t('steel') },
      { key: 'nonferrous_metal', label: t('nonferrous_metal') },
      { key: 'machinery', label: t('machinery') },
      { key: 'electrical_equipment', label: t('electrical_equipment') },
      { key: 'shipbuilding', label: t('shipbuilding') },
      { key: 'automobile', label: t('automobile') },
      { key: 'transportation_equipment', label: t('transportation_equipment') },
      { key: 'precision_equipment', label: t('precision_equipment') },
      { key: 'other_manufacturing', label: t('other_manufacturing') },
      {
        key: 'agriculture_forestry_fishery',
        label: t('agriculture_forestry_fishery'),
      },
      { key: 'mining', label: t('mining') },
      { key: 'construction', label: t('construction') },
      { key: 'trading_company', label: t('trading_company') },
      { key: 'retail', label: t('retail') },
      { key: 'banking', label: t('banking') },
      { key: 'securities', label: t('securities') },
      { key: 'insurance', label: t('insurance') },
      { key: 'other_finance', label: t('other_finance') },
      { key: 'real_estate', label: t('real_estate') },
      { key: 'railway_bus', label: t('railway_bus') },
      { key: 'land_transportation', label: t('land_transportation') },
      { key: 'marine_transportation', label: t('marine_transportation') },
      { key: 'air_transportation', label: t('air_transportation') },
      { key: 'warehouse_logistics', label: t('warehouse_logistics') },
      { key: 'telecommunications', label: t('telecommunications') },
      { key: 'electric_power', label: t('electric_power') },
      { key: 'gas', label: t('gas') },
      { key: 'toc_service', label: t('toc_service') },
      { key: 'tob_service', label: t('tob_service') },
    ],
    [t],
  )

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: {
      title: survey?.title ?? '',
      surveyStartDate: exists(survey)
        ? SurveyOp.getSurveyStartDate(survey)
        : null,
      tamName: metadata?.tamName ?? '',
      tamNumber: metadata?.tamNumber ?? 0,
      // 日本語/英語/キーのいずれが来てもキーに正規化（マイグレーション期間中の互換性のため）
      billingTypeName: normalizeValueToKey(metadata?.billingTypeName),
      purchaseDecisionFrequencyName: normalizeValueToKey(
        metadata?.purchaseDecisionFrequencyName,
      ),
      isFreeName: normalizeValueToKey(metadata?.isFreeName),
      definitionTypeName: normalizeValueToKey(metadata?.definitionTypeName),
      purchaseMainChannelName: normalizeValueToKey(
        metadata?.purchaseMainChannelName,
      ),
      isUseViaSales: normalizeValueToKey(metadata?.isUseViaSales),
      industryName: normalizeValueToKey(metadata?.industryName),
    },
    resolver,
  })
  const titleField = useFormField(control, 'title')
  const surveyStartDateField = useFormField(control, 'surveyStartDate')
  const tamNameField = useFormField(control, 'tamName')
  const tamNumberField = useFormField(control, 'tamNumber')
  const billingTypeNameField = useFormField(control, 'billingTypeName')
  const purchaseDecisionFrequencyNameField = useFormField(
    control,
    'purchaseDecisionFrequencyName',
  )
  const isFreeNameField = useFormField(control, 'isFreeName')
  const definitionTypeNameField = useFormField(control, 'definitionTypeName')
  const purchaseMainChannelNameField = useFormField(
    control,
    'purchaseMainChannelName',
  )
  const isUseViaSalesField = useFormField(control, 'isUseViaSales')
  const industryNameField = useFormField(control, 'industryName')

  const [isVisibleRemoveDialog, toggleIsVisibleRemoveDialog] = useToggle(false)
  const handleRemoveDialogExecute = React.useCallback<
    EH<ExecuteCancelDialogProps, 'onExecute'>
  >(() => {
    toggleIsVisibleRemoveDialog(false)
    onRemove?.()
  }, [onRemove, toggleIsVisibleRemoveDialog])

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    (data) => {
      onSave?.({
        title: data.title,
        metadata: {
          ...R.omit(data, ['title', 'surveyStartDate']),
          surveyStartDate: exists(data.surveyStartDate)
            ? {
                year: data.surveyStartDate.getFullYear(),
                month: data.surveyStartDate.getMonth() + 1,
                day: data.surveyStartDate.getDate(),
              }
            : null,
        },
      })
    },
    [onSave],
  )

  return (
    <Box data-testid={dataTestId} verticalAppearance="stretch-scroll">
      <Box className={styles.grid}>
        <SurveySettingsFormInput label={t('survey_name')}>
          <Input placeholder={t('please_enter_1')} {...titleField} />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('survey_start_date')}>
          <DatePicker
            placeholder={t('please_enter_2')}
            {...surveyStartDateField}
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label="TAM">
          <VStack spacing={2}>
            <Input
              placeholder={t('describe_the_target_population')}
              {...tamNameField}
            />

            <HStack align="center" spacing={2}>
              <NumericTextBox {...tamNumberField} />

              {t('people')}
            </HStack>
          </VStack>
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('commercial_billing_model')}>
          <DropDownList
            data={billingTypeNames}
            dataItemKey="key"
            textField="label"
            value={billingTypeNames.find(
              (item) => item.key === billingTypeNameField.value,
            )}
            onChange={(e) => billingTypeNameField.onChange(e.value?.key ?? '')}
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('purchase_frequency')}>
          <DropDownList
            data={purchaseDecisionFrequencyNames}
            dataItemKey="key"
            textField="label"
            value={purchaseDecisionFrequencyNames.find(
              (item) => item.key === purchaseDecisionFrequencyNameField.value,
            )}
            onChange={(e) =>
              purchaseDecisionFrequencyNameField.onChange(e.value?.key ?? '')
            }
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('availability_of_free_option')}>
          <DropDownList
            data={isFreeNames}
            dataItemKey="key"
            textField="label"
            value={isFreeNames.find(
              (item) => item.key === isFreeNameField.value,
            )}
            onChange={(e) => isFreeNameField.onChange(e.value?.key ?? '')}
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput
          label={t('preffered_nonprefferred_definition')}
        >
          <DropDownList
            data={definitionTypeNames}
            dataItemKey="key"
            textField="label"
            value={definitionTypeNames.find(
              (item) => item.key === definitionTypeNameField.value,
            )}
            onChange={(e) =>
              definitionTypeNameField.onChange(e.value?.key ?? '')
            }
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('channel')}>
          <DropDownList
            data={purchaseMainChannelNames}
            dataItemKey="key"
            textField="label"
            value={purchaseMainChannelNames.find(
              (item) => item.key === purchaseMainChannelNameField.value,
            )}
            onChange={(e) =>
              purchaseMainChannelNameField.onChange(e.value?.key ?? '')
            }
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('purchase_use_via_sales_person')}>
          <DropDownList
            data={isUseViaSalesArray}
            dataItemKey="key"
            textField="label"
            value={isUseViaSalesArray.find(
              (item) => item.key === isUseViaSalesField.value,
            )}
            onChange={(e) => isUseViaSalesField.onChange(e.value?.key ?? '')}
          />
        </SurveySettingsFormInput>

        <SurveySettingsFormInput label={t('category')}>
          <DropDownList
            data={industryNames}
            dataItemKey="key"
            textField="label"
            value={industryNames.find(
              (item) => item.key === industryNameField.value,
            )}
            onChange={(e) => industryNameField.onChange(e.value?.key ?? '')}
          />
        </SurveySettingsFormInput>

        <HStack className={styles.buttons} justifySelf="end" spacing={4}>
          {survey && (
            <StyledButton
              buttonStyle="alert"
              onClick={toggleIsVisibleRemoveDialog}
            >
              {t('delete_7')}
            </StyledButton>
          )}

          <StyledButton
            buttonStyle="success"
            disabled={!isValid}
            onClick={handleSubmit(handleValid)}
          >
            {exists(survey) ? t('save_4') : t('create_new_3')}
          </StyledButton>
        </HStack>
      </Box>

      {isVisibleRemoveDialog && (
        <ExecuteCancelDialog
          mode="delete"
          title={t('delete_survey')}
          onCancel={toggleIsVisibleRemoveDialog}
          onExecute={handleRemoveDialogExecute}
        >
          {t('delete_confirmation', { target: survey?.title })}
        </ExecuteCancelDialog>
      )}
    </Box>
  )
}
